import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/corporation/api';
import PageHeader from '@components/common/PageHeader';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import { CorporationUserUpdateReq } from '@/api/corporation/type';
import Modal from '@components/common/Modal/Modal';

// ── 카카오 주소 타입 선언
declare global {
    interface Window {
        daum: any;
    }
}

// ── 카카오 우편번호 스크립트 로드
const loadKakaoPostcodeScript = (): Promise<void> => {
    return new Promise(resolve => {
        if (window.daum?.Postcode) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src =
            'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
        script.async = true;
        script.onload = () => resolve();
        document.head.appendChild(script);
    });
};

const CorporationUserDetail = () => {
    const JOIN_TYPE = {
        NAVER: 0,
        KAKAO: 1,
        GOOGLE: 2,
        ID: 9,
    } as const;

    const JOIN_TYPE_LABELS: Record<number, string> = {
        [JOIN_TYPE.NAVER]: '네이버',
        [JOIN_TYPE.KAKAO]: '카카오',
        [JOIN_TYPE.GOOGLE]: '구글',
        [JOIN_TYPE.ID]: 'ID',
    };

    const [formData, setFormData] = useState({
        userIdx: 0,
        joinType: JOIN_TYPE.ID as number,
        userId: '',
        userName: '',
        phone: '',
        email: '',
        address: '',
        apiKey: '',
        apiExpDt: '',
        regDt: '',
    });

    // ── 주소 모달 상태
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const postcodeContainerRef = useRef<HTMLDivElement>(null);

    // ── 사용자 정보 조회
    const getUserInfo = useCallback(async () => {
        const result = await api.corporationUserInfo();
        setFormData({
            userIdx: result.userIdx ?? 0,
            joinType: result.joinType ?? JOIN_TYPE.ID,
            userId: result.userId ?? '',
            userName: result.userName ?? '',
            phone: result.phone ?? '',
            email: result.email ?? '',
            address: result.address ?? '',
            apiKey: result.apiKey ?? '',
            apiExpDt: result.apiExpDt ?? '',
            regDt: result.regDt ?? '',
        });
    }, []);

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // ── 주소 모달 오픈
    const handleAddressSearch = async () => {
        await loadKakaoPostcodeScript();
        setIsAddressModalOpen(true);
    };

    // ── 모달 열린 후 카카오 우편번호 embed
    useEffect(() => {
        if (!isAddressModalOpen) return;

        // DOM 렌더링 후 embed
        const timer = setTimeout(() => {
            if (!postcodeContainerRef.current || !window.daum?.Postcode) return;

            // 기존 내용 초기화
            postcodeContainerRef.current.innerHTML = '';

            new window.daum.Postcode({
                oncomplete: (data: any) => {
                    const fullAddress = data.roadAddress || data.jibunAddress;
                    handleInputChange('address', fullAddress);
                    setIsAddressModalOpen(false);
                },
                // embed 모드 필수 옵션
                width: '100%',
                height: '100%',
            }).embed(postcodeContainerRef.current);
        }, 100);

        return () => clearTimeout(timer);
    }, [isAddressModalOpen]);

    // ── API KEY 발급
    const handleGenerateApiKey = async () => {
        await Alert.confirmAndExecute(
            '키발급을 진행하시겠습니까?',
            '이전키를 사용할수 없게됩니다. 주의하세요.',
            true,
            async () => {
                await api
                    .corporationApiKeyReissue(formData.userIdx)
                    .then(async () => {
                        await getUserInfo();
                    });
            },
        );
    };

    // ── 저장
    const handleSave = async () => {
        await Alert.confirmAndExecute(
            '저장하시겠습니까?',
            '잘못된 정보가 없는지 주의하세요.',
            true,
            async () => {
                const updateRequest: CorporationUserUpdateReq = {
                    userIdx: formData.userIdx,
                    userName: formData.userName,
                    phone: formData.phone,
                    email: formData.email,
                    address: formData.address,
                };
                await api
                    .corporationUserUpdate(updateRequest)
                    .then(async () => {
                        await getUserInfo();
                    });
            },
        );
    };

    useEffect(() => {
        getUserInfo();
        loadKakaoPostcodeScript();
    }, [getUserInfo]);

    return (
        <>
            {/* ── 주소 검색 모달 ── */}
            {isAddressModalOpen && (
                <Modal
                    title="주소 검색"
                    size="md"
                    onClose={() => setIsAddressModalOpen(false)}
                    hideFooter
                >
                    <div
                        ref={postcodeContainerRef}
                        style={{ width: '100%', height: 460 }}
                    />
                </Modal>
            )}

            <PageHeader
                title="내 정보"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '내 정보', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        내 정보를 확인하고 수정하세요.
                    </div>
                </div>
                <div className="card-body">
                    {/* 아이디 (비활성) */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                아이디 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.userId}
                                disabled
                            />
                        </div>
                    </div>

                    {/* 대행사명 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="대행사명을 입력해주세요."
                                value={formData.userName}
                                onChange={e =>
                                    handleInputChange(
                                        'userName',
                                        e.target.value,
                                    )
                                }
                            />
                        </div>
                    </div>

                    {/* 전화번호 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                전화번호 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="tel"
                                className="form-control"
                                placeholder="전화번호를 입력해주세요."
                                value={formData.phone}
                                onChange={e =>
                                    handleInputChange('phone', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    {/* 이메일 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                이메일<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="email"
                                className="form-control"
                                placeholder="이메일을 입력해주세요."
                                value={formData.email}
                                onChange={e =>
                                    handleInputChange('email', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    {/* 주소 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                주소 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="주소찾기 버튼을 눌러 주소를 선택해주세요."
                                    value={formData.address}
                                    readOnly
                                    style={{
                                        cursor: 'default',
                                        background: '#f8f9fa',
                                    }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-wave waves-effect waves-light"
                                    onClick={handleAddressSearch}
                                >
                                    <i className="ri-map-pin-line me-2 d-inline-block lh-1"></i>
                                    주소찾기
                                </button>
                            </div>
                            <small className="text-muted mt-1 d-block">
                                <i className="ri-information-line me-1"></i>
                                최초 주소값은 접속 IP 기반으로 등록됩니다.
                            </small>
                        </div>
                    </div>

                    {/* API KEY */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                API KEY :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="API KEY 발급을 눌러주세요."
                                    value={formData.apiKey}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-wave waves-effect waves-light"
                                    onClick={handleGenerateApiKey}
                                >
                                    <i className="ri-refresh-line me-2 d-inline-block lh-1"></i>
                                    키발급
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* API KEY 만료일자 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                API KEY 만료일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.apiExpDt}
                                placeholder="API KEY를 발급받지 않았습니다."
                                disabled
                            />
                        </div>
                    </div>

                    {/* 가입일자 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                가입일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.regDt}
                                disabled
                            />
                        </div>
                    </div>

                    {/* 가입유형 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                가입유형 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={
                                    JOIN_TYPE_LABELS[formData.joinType] ?? '-'
                                }
                                disabled
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CorporationUserDetail;
